import {
  PurchaseAgentDeactivateButton,
  PurchaseAgentLimitsButton,
  PurchaseAgentPrimaryStateButton,
  PurchaseAgentStateLabel,
} from '@components/purchase-agent-state';
import { PurchaseAgentDrawer } from '@components/PurchaseAgentDrawer';
import { DrawerContent, DrawerHeader, DrawerStatic } from '@components/ui';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { styled } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from 'src/constants';
import { useChangePurchaseAgentState, useDeletePurchaseAgent, usePurchaseAgents } from 'src/hooks/usePurchaseAgents';
import useRole from 'src/hooks/useRole';
import { useUser } from 'src/hooks/useUser';
import { PurchaseAgentListItem } from 'src/modules/generated/api';
import { getStateActions } from 'src/modules/purchase-agent';
import UserRole from 'src/types/UserRoles';

const SCPurchaseAgentItem = styled('div')({
  paddingInline: 12,
  paddingBlock: 8,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  minHeight: 42,

  '& .pa_item__actions': {
    gap: 4,
  },

  '&:hover': {
    background: grey[100],
  },

  '&:hover .pa_item__actions': {
    display: 'inline-flex',
  },
});

const useStyles = makeStyles((theme) => ({
  addButton: {
    color: theme.palette.intelligence.main,
  },
  title: {
    fontWeight: 'bold',
  },
  manageLink: {
    color: theme.palette.text.primary,
  },
}));

const PurchaseAgentItem = ({
  agent,
  onEdit,
  canEdit,
  canDelete,
}: {
  agent: PurchaseAgentListItem;
  onEdit: (id: string) => void;
  canEdit: boolean;
  canDelete: boolean;
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: deletePurchaseAgent, isLoading: isDeleting } = useDeletePurchaseAgent();
  const { mutate: changeState } = useChangePurchaseAgentState();
  const agentId = agent.id?.purchaseAgentId;
  const { canDeactivate } = getStateActions(agent.state);

  const handleConfirm = () => {
    const id = agent.id?.purchaseAgentId;
    if (id) {
      deletePurchaseAgent(id, { onSettled: () => setConfirmOpen(false) });
    } else {
      setConfirmOpen(false);
    }
  };

  const handleDeactivate = () => {
    if (agentId) {
      changeState({ id: agentId, action: 'deactivate' }, { onSuccess: () => setConfirmOpen(false) });
    } else {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <SCPurchaseAgentItem>
        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <PurchaseAgentPrimaryStateButton agent={agent} />
          <PurchaseAgentStateLabel state={agent.state} dotOnly />
          <Typography
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {agent.name}
          </Typography>
        </div>
        <div className="pa_item__actions">
          <PurchaseAgentDeactivateButton agent={agent} />
          <PurchaseAgentLimitsButton agent={agent} />
          {canEdit && (
            <Tooltip title={t('common.edit')} placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  if (agentId) onEdit(agentId);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t('purchaseAgent.delete.tooltip')} placement="top">
              <IconButton size="small" onClick={() => setConfirmOpen(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </SCPurchaseAgentItem>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('purchaseAgent.delete.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('purchaseAgent.delete.confirmText')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary" variant="outlined" size="small">
            {t('common.cancel')}
          </Button>
          {canDeactivate && (
            <Button onClick={handleDeactivate} color="secondary" variant="contained" size="small">
              {t('purchaseAgent.deactivate.tooltip')}
            </Button>
          )}
          <Button onClick={handleConfirm} color="primary" variant="contained" size="small" disabled={isDeleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SectionLabel = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    color="textSecondary"
    style={{ display: 'block', paddingInline: 12, paddingBlock: 4, fontWeight: 600 }}
  >
    {label}
  </Typography>
);

export const PurchaseAgentsView = (): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    data: purchaseAgents = [],
    isLoading: isPurchaseAgentsLoading,
    isError: isPurchaseAgentsError,
  } = usePurchaseAgents();
  const { data: user } = useUser();
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<string>();

  const handleEdit = (id: string) => {
    setEditingAgentId(id);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingAgentId(undefined);
  };

  const { mine, others } = useMemo(() => {
    const userId = user?.user?.id;
    return {
      mine: purchaseAgents.filter((pa) => pa.created?.by?.id === userId),
      others: purchaseAgents.filter((pa) => pa.created?.by?.id !== userId),
    };
  }, [purchaseAgents, user?.user?.id]);

  return (
    <DrawerStatic>
      <DrawerHeader>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography className={classes.title}>{t('purchaseAgent.page.title')}</Typography>
          <Grid item style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MuiLink href={`${RoutePaths.searchAgents}#kaufagenten`} className={classes.manageLink}>
              {t('purchaseAgent.manage')}
            </MuiLink>
            <Tooltip title={t('purchaseAgent.drawer.title')}>
              <IconButton
                size="small"
                className={classes.addButton}
                onClick={() => {
                  setEditingAgentId(undefined);
                  setDrawerOpen(true);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DrawerHeader>
      <DrawerContent>
        {isPurchaseAgentsLoading ? (
          <Grid container justifyContent="center" style={{ paddingBlock: 16 }}>
            <CircularProgress size={24} />
          </Grid>
        ) : null}
        {isPurchaseAgentsError ? <Typography color="error">{t('purchaseAgent.list.loadError')}</Typography> : null}
        {mine.length > 0 && (
          <>
            <SectionLabel label={t('purchaseAgent.list.mine')} />
            {mine.map((agent) => (
              <PurchaseAgentItem key={agent.id?.purchaseAgentId} agent={agent} onEdit={handleEdit} canEdit canDelete />
            ))}
          </>
        )}
        {mine.length > 0 && others.length > 0 && <Divider style={{ marginBlock: 4 }} />}
        {others.length > 0 && (
          <>
            <SectionLabel label={t('purchaseAgent.list.others')} />
            {others.map((agent) => (
              <PurchaseAgentItem
                key={agent.id?.purchaseAgentId}
                agent={agent}
                onEdit={handleEdit}
                canEdit={false}
                canDelete={isAdmin}
              />
            ))}
          </>
        )}
      </DrawerContent>

      <PurchaseAgentDrawer open={isDrawerOpen} onClose={handleDrawerClose} editPurchaseAgentId={editingAgentId} />
    </DrawerStatic>
  );
};
